import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useLocation, withRouter } from "react-router-dom";
import { payDueBooking } from "app/pages/Account/MyBookings/myBookingsActionCreators";
import {
	buildValidator,
	fields,
} from "app/pages/Account/MyBookings/MyBookingsPayment/MyBookingsPaymentSchema.js";
import reduxFormSubmitter from "app/utils/form/reduxFormSubmitter";
import { encryptCreditCard } from "app/utils/creditCardUtils";
import { getStore } from "app/configureStore";
import MyBookingsPayment from "app/pages/Account/MyBookings/MyBookingsPayment/MyBookingsPayment";
import {
	BOOK_EXTERNAL_PAYMENT_STATUS,
	BOOK_STATUS,
	EXTERNAL_PAYMENT_STATUS,
	HTTP_STATUS_CODES,
	MY_BOOKINGS_STATUS,
	STRATEGY,
} from "app/constants";
import {
	sendTagOn3DSRedirect,
	sendTagOnExternalPayment,
	sendTagOnMyBookingsPaymentFailed,
} from "app/utils/analytics";
import { parseAmount } from "app/utils/utils";
import { initialize, stopSubmit } from "redux-form";
import { BOOK_EXTERNAL_HTML_REQUEST } from "app/actionTypes";
import {
	bookExternalPayment,
	fetchExternalPaymentCallback,
	updateExternalBookStatus,
} from "app/pages/Booking/Payment/paymentActionCreators";
import {
	finishLoadingBar,
	showLoadingBar,
} from "app/pages/.shared/LoadingBar/loadingBarActionCreators";
import get from "lodash/get";
import { getContextCallCenter } from "app/pages/Booking/bookingSelectors";
import { logout } from "app/pages/Auth/AuthActions";
import { getPaymentErrorMessage } from "app/utils/paymentUtils";
import { parseQueries } from "app/utils/routerUtils";
import validationMessages from "app/utils/form/validationMessages";
import { bindActionCreators } from "redux";
import { updateEmail } from "app/reducers/emailActionCreators";
import { strategyShape } from "app/utils/propTypes";
import { getBrandDisplayName } from "app/reducers/partnerSelector";

const MyBookingsPaymentContainer = props => {
	const {
		formInitialValues,
		initializeForm,
		amount,
		showExternalPaymentError,
		updateExternalBookStatus,
		onPaymentSuccess,
	} = props;

	const location = useLocation();
	const query = parseQueries(location.search);

	const handleExternalPaymentStatus = useCallback(externalPaymentStatus => {
		if (externalPaymentStatus) {
			updateExternalBookStatus(externalPaymentStatus);

			if (
				externalPaymentStatus === EXTERNAL_PAYMENT_STATUS.EXTERNAL_PAYMENT_STATUS_ERROR ||
				externalPaymentStatus === EXTERNAL_PAYMENT_STATUS.EXTERNAL_PAYMENT_STATUS_ABORTED
			) {
				sendTagOnExternalPayment(externalPaymentStatus);
				showExternalPaymentError(externalPaymentStatus);
			} else if (
				externalPaymentStatus === EXTERNAL_PAYMENT_STATUS.EXTERNAL_PAYMENT_STATUS_SUCCESS
			) {
				if (typeof onPaymentSuccess === "function") {
					onPaymentSuccess();
				}
			}
		}
	}, []);

	useEffect(() => {
		initializeForm(formInitialValues);
	}, [amount]);

	useEffect(() => {
		handleExternalPaymentStatus(query.externalPaymentStatus);
	}, [query.externalPaymentStatus]);

	return <MyBookingsPayment {...props} />;
};

MyBookingsPaymentContainer.propTypes = {
	formInitialValues: PropTypes.object,
	initializeForm: PropTypes.func,
	amount: PropTypes.number,
	logout: PropTypes.func,
	bookExternalPayment: PropTypes.func,
	showExternalPaymentError: PropTypes.func,
	onPaymentSuccess: PropTypes.func,
	updateExternalBookStatus: PropTypes.func,
	strategy: strategyShape,
};

const mapFormToPayload = (values, ownProps) => {
	const creditCard = values.payment.card;

	const payload = {
		brand: ownProps.brand,
		shop: ownProps.shop,
		email: ownProps.email,
		baseUrl: window.location.origin,
		bookingReference: ownProps.bookingReference,
		creditCardToken: encryptCreditCard({
			holderName: creditCard.holderName,
			expiryMonth: creditCard.expiryMonth,
			expiryYear: "20" + creditCard.expiryYear, // expiryYear est sur 2 digits or il faut 4 digits pour passer la validation adyen
			cvc: creditCard.cvc,
			number: creditCard.number,
			generationtime: new Date().toISOString(),
		}),
	};

	const creditCardAmountToPay = parseAmount(creditCard.amount);

	if (creditCardAmountToPay > 0) {
		payload.amount = creditCardAmountToPay;
	}

	return payload;
};

const onSubmitSuccess = (response, ownProps, dispatch) => {
	const { bookingReference } = ownProps.match.params;

	if (response.status === HTTP_STATUS_CODES.OK) {
		if (response.data.redirectPaymentURL && response.data.status === BOOK_STATUS.SUCCESS) {
			window.location = response.data.redirectPaymentURL;
		} else if (
			response.data.status === BOOK_STATUS.EXTERNAL_REDIRECT_REQUEST &&
			response.data.externalUrl
		) {
			sendTagOn3DSRedirect();
			ownProps.history.push({
				pathname: "/external-redirect",
				state: {
					redirectParams: response.data.params,
					externalUrl: response.data.externalUrl,
				},
			});
		} else if (
			response.data.status === BOOK_STATUS.EXTERNAL_HTML_REQUEST &&
			response.data.htmlPayload // scoring casino et (3DS2)
		) {
			dispatch({
				type: BOOK_EXTERNAL_HTML_REQUEST,
			});

			const { callbackDelay, htmlPayload } = response.data;
			const callbackUrl = response.data.callback_url;

			fetchExternalPaymentCallback({
				callbackDelay,
				callbackUrl,
				htmlPayload,
			})
				.then(externalPaymentStatus => {
					if (externalPaymentStatus) {
						ownProps.history.push({
							pathname: `/account/mybookings/${bookingReference}`,
							search: `?externalPaymentStatus=${externalPaymentStatus}`,
						});
					}
				})
				.catch(() => {
					ownProps.history.push({
						pathname: `/account/mybookings/${bookingReference}`,
						search: `?externalPaymentStatus=${
							BOOK_STATUS.EXTERNAL_PAYMENT_STATUS_ERROR
						}`,
					});
					dispatch(finishLoadingBar());
				});
		} else if (response.data.status === MY_BOOKINGS_STATUS.SUCCESS) {
			if (typeof ownProps.onPaymentSuccess === "function") {
				ownProps.onPaymentSuccess();
			}
		}
	}
};

const onSubmitFail = (response, ownProps, reject, dispatch) => {
	const httpStatus = response.status;
	const httpErrorMessage = get(response, "data.message");

	const isHttpError =
		!httpStatus ||
		httpStatus === HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR ||
		httpStatus === HTTP_STATUS_CODES.GATEWAY_TIMEOUT ||
		httpStatus === HTTP_STATUS_CODES.BAD_REQUEST;

	const state = getStore().getState();
	const contextCallCenter = getContextCallCenter({
		isPaymentPage: true,
	})(state);

	const phoneNumber = contextCallCenter.displayPhone
		? contextCallCenter.displayPhone.join(" ")
		: "";

	if (isHttpError) {
		// TODO Lorsqu'il s'agit du site institutionnel, il faut revoir la page technical-error car elle n'existe pas
		ownProps.history.push({
			pathname: "/technical-error",
			state: {
				errorMessage: httpErrorMessage,
				brand: ownProps.brand,
				shop: ownProps.shop,
			},
		}); // push car on veut rester sur la page de my bookings si back navigateur
	} else if (httpStatus === HTTP_STATUS_CODES.FORBIDDEN) {
		dispatch(logout());
		if (ownProps.strategy === STRATEGY.OAUTH) {
			ownProps.history.push({ pathname: "/technical-error/sso" });
		} else if (ownProps.strategy === STRATEGY.TRANSACTION_FIRST) {
			ownProps.history.push({
				pathname: "/technical-error",
				state: {
					errorMessage: httpErrorMessage,
				},
			});
		} else {
			ownProps.history.push({
				pathname: "/booking/auth",
				search: `?onauthsuccess=${ownProps.location.pathname}`,
			}); // replace pour éviter un back navigateur sur la page de quotation
		}
	} else {
		dispatch(finishLoadingBar());
		reject({ _error: getPaymentErrorMessage(response, phoneNumber) });
	}

	if (isHttpError) {
		sendTagOnMyBookingsPaymentFailed(response.status);
	} else if (response.data.status) {
		sendTagOnMyBookingsPaymentFailed(response.data.status);
	}
};

const mapStateToProps = (state, ownProps) => {
	return {
		formInitialValues: { payment: { card: { amount: ownProps.amount } } },
		shop: state.shop,
		strategy: state.partner.strategy,
		brandDisplayName: getBrandDisplayName(state),
	};
};

const mapDispatchToProps = dispatch => {
	return {
		...bindActionCreators(
			{
				logout,
				updateEmail,
				bookExternalPayment,
				updateExternalBookStatus,
				finishLoadingBar,
				showLoadingBar,
			},
			dispatch
		),
		initializeForm: initializeValues => {
			dispatch(initialize("my-bookings-payment-form", initializeValues));
		},
		showExternalPaymentError: status => {
			if (status === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_STATUS_REFUSED) {
				dispatch(
					stopSubmit("my-bookings-payment-form", {
						_error: {
							id: "error.payment.external.technical.error.not.enough.balance",
						},
					})
				);
			} else if (status === BOOK_STATUS.SESSION_EXPIRED) {
				dispatch(
					stopSubmit("my-bookings-payment-form", {
						_error: {
							id: "error.booking.session.expired",
						},
					})
				);
			} else if (
				status === EXTERNAL_PAYMENT_STATUS.EXTERNAL_PAYMENT_STATUS_ERROR ||
				status === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_TECHNICAL_ERROR
			) {
				const state = getStore().getState();
				const contextCallCenter = getContextCallCenter({
					isPaymentPage: true,
				})(state);

				const phoneNumber = contextCallCenter.displayPhone
					? contextCallCenter.displayPhone.join(" ")
					: "";
				dispatch(
					stopSubmit("my-bookings-payment-form", {
						_error: {
							id: validationMessages.externalPaymentError.id,
							values: { telephone: phoneNumber },
						},
					})
				);
			} else if (status === BOOK_STATUS.CREDIT_CARD_INFO) {
				dispatch(
					stopSubmit("my-bookings-payment-form", {
						_error: {
							id: "error.payment.credit.card.info",
						},
					})
				);
			} else if (status === BOOK_STATUS.CREDIT_CARD_TECHNICAL_ERROR) {
				dispatch(
					stopSubmit("my-bookings-payment-form", {
						_error: {
							id: "error.payment.technical.error",
						},
					})
				);
			} else if (status === BOOK_STATUS.CREDIT_CARD_REFUSED) {
				dispatch(
					stopSubmit("my-bookings-payment-form", {
						_error: {
							id: "error.payment.credit.card.refused.not.enough.balance",
						},
					})
				);
			}
		},
	};
};

const reduxFormConfig = {
	form: "my-bookings-payment-form",
	fields,
	validateForm: (values, ownProps) => {
		return buildValidator(ownProps.amount)(values, ownProps);
	},
	mapFormToPayload,
	actionCreator: payDueBooking,
	onSubmitSuccess,
	onSubmitFail,
};

export default withRouter(
	reduxFormSubmitter(reduxFormConfig, mapStateToProps, mapDispatchToProps)(
		MyBookingsPaymentContainer
	)
);
